import { EventAggregator } from 'aurelia-event-aggregator';
import { I18N } from 'aurelia-i18n';
import { Router } from 'aurelia-router';
import { anyString, anything, instance, mock, when } from 'ts-mockito';
import { Business } from '../../models/business';
import { Account, Customer } from '../../models/customer';
import { ApplePaySettings, ApplePayUtil } from '../../models/digital-wallet';
import { SinglePaymentRouteSettings } from '../../models/single-payment/single-payment-route-settings';
import { TemplateData } from '../../models/template-data';
import { State } from '../../services/global-state';
import { DigitalWalletPaymentMethod, HpfTransactionService } from '../../services/hpf-transaction-service';
import { PayrazrService } from '../../services/payrazr';
import { PortalTemplateTypes, TemplateService } from '../../services/template-service';
import { PaymentItemTable } from '../../smart/components/payment-item-table/payment-item-table';
import { ComponentValidationResult } from '../../smart/components/payment-item-table/validation/component-validation-result';
import { NumberHelper } from '../../smart/helpers/number-helper';
import { AccountPaymentItem } from '../../smart/models/account-payment-item';
import { ServiceResponse } from '../../smart/models/service-response';
import { SinglePayment } from './single-payment';

describe('NewPortalWeb', () => {
	describe('pages', () => {
		describe('single-payment', () => {
			let component: SinglePayment;
			let stubState: State;

			const mockI18N: I18N = mock(I18N);
			const mockEventAggregator: EventAggregator = mock(EventAggregator);
			const mockCustomer: Customer = mock(Customer);
			const mockCustomerAccount: Account = mock(Account);
			const mockBusiness: Business = mock(Business);
			const mockRouter: Router = mock(Router);
			const mockPayrazrService: PayrazrService = mock(PayrazrService);
			const mockHpfTransactionService: HpfTransactionService = mock(HpfTransactionService);
			const mockTemplateService: TemplateService = mock(TemplateService);
			const mockPaymentItemTableViewModel: PaymentItemTable = mock(PaymentItemTable);
			const mockApplePaySettings: ApplePaySettings = mock(ApplePaySettings);
			const mockDigitalWalletPaymentMethod: DigitalWalletPaymentMethod = mock(DigitalWalletPaymentMethod);
			const mockAccountPaymentItems: AccountPaymentItem[] = [mock(AccountPaymentItem)];

			beforeEach(() => {
				stubState = new State(
					instance(mockI18N),
					instance(mockEventAggregator));

				stubState.customer = instance(mockCustomer);

				let customerAccount = instance(mockCustomerAccount);
				customerAccount.portfolioName = 'loremIpsum';
				customerAccount.customerAccountKey = 1;
				stubState.customer.accounts = [customerAccount];

				let business = instance(mockBusiness);
				business.portalSettings = {
					fields: {
						paymentAmount: {
							label: 'payment amount'
						}
					}
				};
				stubState.business = business;

				const i18next = {
					t: (a, b) => a
				};

				// Mocking a method of a plain object (non mocked instance).
				// This is saying that, when you encounter a reference to mockI18N.i18next
				// return the reference specified.
				when(mockI18N.i18next).thenReturn(i18next);
				// Then we are mocking the i18next.t() method.
				spyOn(i18next, 't').and.returnValue('localized');

				component = new SinglePayment(
					stubState,
					instance(mockRouter),
					instance(mockPayrazrService),
					instance(mockTemplateService),
					instance(mockI18N),
					instance(mockHpfTransactionService));

				mockApplePaySettings.isApplePay = () => true;

				component.paymentItemTableViewModel = {
					getSelectedItems: () => null
				};
			});

			afterEach(() => {
				stubState = null;
				component = null;
			});

			it('should initialize SinglePayment component', () => {
				expect(component).toBeTruthy();
			});

			describe('activate()', () => {
				it('should initialize customer, customer account(s), and payment settings from global state and routeConfig', () => {
					// Mock a method of an existing instance (as opposed to mocking a method of a mocked instance).
					spyOn(component, 'getSingleFutureAllowedForSelectedAccounts').and.returnValue(true);

					spyOn(stubState.business, 'fetchPortfolioSettings')
						.withArgs(stubState.accounts[0].portfolioName)
						.and.returnValue({
							merchantSettings: {
								maxFutureDays: 30,
								feeSettings: {
									serviceFeeEnabled: false
								}
							},
							settings: {
								paymentTypes: {
									maxFutureDays: 45,
									allowFuture: true
								}
							}
						});

					let routeSettings = new SinglePaymentRouteSettings();
					routeSettings.payInFull = true;
					routeSettings.isPayOffEarly = false;
					routeSettings.selectedCustomerAccountKeys = [stubState.customer.accounts[0].customerAccountKey];

					let routeConfig = {
						settings: routeSettings
					};

					component.activate({}, routeConfig);

					expect(component.getSingleFutureAllowedForSelectedAccounts).toHaveBeenCalled();
					expect(component.customer).toBe(stubState.customer);
				});
			});

			describe('getSingleFutureAllowedForSelectedAccounts()', () => {
				let tests = [
					{
						expectation: 'should iterate through customer accounts and return true per portfolio settings',
						allowFuture: true
					},
					{
						expectation: 'should iterate through customer accounts and return false per portfolio settings',
						allowFuture: false
					}
				];

				tests.forEach(x => {
					it(x.expectation, () => {
						spyOn(stubState.business, 'fetchPortfolioSettings')
							.withArgs(stubState.accounts[0].portfolioName)
							.and.returnValue({
								settings: {
									paymentTypes: {
										allowFuture: x.allowFuture
									}
								}
							});

						component.accounts = stubState.customer.accounts;
						let response = component.getSingleFutureAllowedForSelectedAccounts();
						expect(stubState.business.fetchPortfolioSettings).toHaveBeenCalled();
						expect(response).toBe(x.allowFuture);
					});
				});
			});

			describe('next()', () => {
				beforeEach(() => {
					spyOn(component, 'updateSubtotal').and.stub();
					spyOn(component, 'getConfirmationTemplateData').and.stub();
					spyOn(component, 'setApplePaySettingsFields').and.stub();
					spyOn(component, 'showConfirmation').and.stub();
					spyOn(window, 'confirm').and.returnValue(true);
				});

				it('should execute with no errors', () => {
					// Arrange
					const pageErrors = [];
					const validationErrors = [];

					let componentValidationResult: ComponentValidationResult = new ComponentValidationResult();
					componentValidationResult.isValid = true;
					componentValidationResult.confirm = false;
					componentValidationResult.errorMessages = validationErrors;
					componentValidationResult.confirmErrorMessageIndex = -1;
					component.paymentItemTableViewModel = mockPaymentItemTableViewModel;

					spyOn(mockPaymentItemTableViewModel, 'validate').and.returnValue(componentValidationResult);
					spyOn(component, 'getValidationErrors').and.returnValue(pageErrors);

					// Act
					component.next({
						detail: {
							errors: pageErrors
						}
					});

					// Assert
					expect(mockPaymentItemTableViewModel.validate).toHaveBeenCalled();
					expect(component.getValidationErrors).toHaveBeenCalled();
					expect(component.updateSubtotal).toHaveBeenCalled();
					expect(component.getConfirmationTemplateData).toHaveBeenCalled();
					expect(component.showConfirmation).toHaveBeenCalled();
				});

				it('should execute with confirmOk', () => {
					// Arrange
					const pageErrors = [];
					const validationErrors = [
						// confirmation error
						'lorem ipsum'
					];

					let componentValidationResult: ComponentValidationResult = new ComponentValidationResult();
					componentValidationResult.isValid = false;
					componentValidationResult.confirm = true;
					componentValidationResult.errorMessages = validationErrors;
					componentValidationResult.confirmErrorMessageIndex = 0;
					component.paymentItemTableViewModel = mockPaymentItemTableViewModel;

					spyOn(mockPaymentItemTableViewModel, 'validate').and.returnValue(componentValidationResult);
					spyOn(component, 'getValidationErrors').and.returnValue(pageErrors);

					// Act
					component.next({
						detail: {
							errors: pageErrors
						}
					});

					// Assert
					expect(mockPaymentItemTableViewModel.validate).toHaveBeenCalled();
					expect(component.getValidationErrors).toHaveBeenCalled();
					expect(component.updateSubtotal).toHaveBeenCalled();
					expect(component.getConfirmationTemplateData).toHaveBeenCalled();
					expect(component.showConfirmation).toHaveBeenCalled();
				});

				it('should display confirmation alert and render validation errors', () => {
					// Arrange
					const pageErrors = [];
					const validationErrors = [
						// validation error
						'lorem ipsum',
						// confirmation error
						'dolor sit amet'
					];

					let componentValidationResult: ComponentValidationResult = new ComponentValidationResult();
					componentValidationResult.isValid = false;
					componentValidationResult.confirm = true;
					componentValidationResult.errorMessages = validationErrors;
					componentValidationResult.confirmErrorMessageIndex = 1;
					component.paymentItemTableViewModel = mockPaymentItemTableViewModel;

					spyOn(mockPaymentItemTableViewModel, 'validate').and.returnValue(componentValidationResult);
					spyOn(component, 'getValidationErrors').and.returnValue(pageErrors);

					// Act
					component.next({
						detail: {
							errors: pageErrors
						}
					});

					// Assert
					expect(mockPaymentItemTableViewModel.validate).toHaveBeenCalled();
					expect(window.confirm).toHaveBeenCalled();
				});

				it('should render validation errors', () => {
					// Arrange
					const pageErrors = [];
					const validationErrors = [
						// validation error
						'lorem ipsum',
					];

					let componentValidationResult: ComponentValidationResult = new ComponentValidationResult();
					componentValidationResult.isValid = false;
					componentValidationResult.confirm = false;
					componentValidationResult.errorMessages = validationErrors;
					componentValidationResult.confirmErrorMessageIndex = -1;
					component.paymentItemTableViewModel = mockPaymentItemTableViewModel;

					spyOn(mockPaymentItemTableViewModel, 'validate').and.returnValue(componentValidationResult);
					spyOn(component, 'getValidationErrors').and.returnValue(pageErrors);

					// Act
					component.next({
						detail: {
							errors: pageErrors
						}
					});

					// Assert
					expect(mockPaymentItemTableViewModel.validate).toHaveBeenCalled();
				});

				it('should render page errors', () => {
					// Arrange
					const pageErrors = [
						// page errors
						'lorem ipsum'
					];
					const validationErrors = [];

					let componentValidationResult: ComponentValidationResult = new ComponentValidationResult();
					componentValidationResult.isValid = true;
					componentValidationResult.confirm = false;
					componentValidationResult.errorMessages = validationErrors;
					componentValidationResult.confirmErrorMessageIndex = -1;
					component.paymentItemTableViewModel = mockPaymentItemTableViewModel;

					spyOn(mockPaymentItemTableViewModel, 'validate').and.returnValue(componentValidationResult);
					spyOn(component, 'getValidationErrors').and.returnValue(pageErrors);

					// Act
					component.next({
						detail: {
							errors: pageErrors
						}
					});

					// Assert
					expect(mockPaymentItemTableViewModel.validate).toHaveBeenCalled();
					expect(component.validationErrors.length).toEqual(1);
				});

				it('should set Apple Pay recurring settings when is Apple Pay', () => {
					// Arrange
					const pageErrors = [];
					const validationErrors = [];

					let componentValidationResult: ComponentValidationResult = new ComponentValidationResult();
					componentValidationResult.isValid = true;
					componentValidationResult.confirm = false;
					componentValidationResult.errorMessages = validationErrors;
					componentValidationResult.confirmErrorMessageIndex = -1;
					component.paymentItemTableViewModel = mockPaymentItemTableViewModel;

					const applePaySettings: ApplePaySettings = new ApplePaySettings(mockI18N, stubState);
					applePaySettings.isApplePay = () => true;
					component.isDigitalWallet = true;

					spyOn(mockPaymentItemTableViewModel, 'validate').and.returnValue(componentValidationResult);
					spyOn(component, 'getValidationErrors').and.returnValue(pageErrors);

					// Act
					component.next({
						detail: {
							errors: pageErrors,
							digitalWalletSettings: applePaySettings
						}
					});

					// Assert
					expect(mockPaymentItemTableViewModel.validate).toHaveBeenCalled();
					expect(component.setApplePaySettingsFields).toHaveBeenCalledTimes(1);
					expect(component.validationErrors.length).toEqual(0);
				});
			});

			describe('updateSubtotal()', () => {
				it('should get subtotal from paymentItemTableViewModel', () => {
					const mockPaymentItemTableViewModel: PaymentItemTable = mock(PaymentItemTable);

					component.paymentItemTableViewModel = mockPaymentItemTableViewModel;

					let expectedSubtotal = 10;
					spyOn(mockPaymentItemTableViewModel, 'getTotalEnteredAmountForSelectedItems')
						.and.returnValue(expectedSubtotal);

					component.updateSubtotal();

					expect(mockPaymentItemTableViewModel.getTotalEnteredAmountForSelectedItems).toHaveBeenCalled();
					expect(component.subtotal).toEqual(expectedSubtotal);
				});
			});

			describe('getConfirmationTemplateData()', () => {
				xit('should get confirmation template data', () => {
					let paymentInformation = {
						card: {
							cardNumber: '4444333322221111',
							expirationDate: '11/25'
						}
					};

					when(component.paymentInformation).thenReturn(paymentInformation);
					spyOn<any>(paymentInformation, 'card')
						.and.returnValue(paymentInformation.card);

					spyOn<any>(component, 'getAccountsToPayForSubmission').and.stub();

					component.getConfirmationTemplateData();

					expect(mockI18N.i18next.t(anyString(), anyString())).toHaveBeenCalled();
					expect(component['getAccountsToPayForSubmission']).toHaveBeenCalled();
					expect(component.confirmationTemplateData).toBeInstanceOf(TemplateData);
				});
			});

			describe('getValidationErrors(paymentInformation: any)', () => {
				it('should be invalid payment date', () => {
					// arrange
					let paymentInformation = {
						date: 'foo'
					};

					// act
					let result = component.getValidationErrors(paymentInformation);

					// assert
					expect(result.length).toBeGreaterThan(0);
				});

				it('should be post-date cannot be in the past', () => {
					// arrange
					let msCurrentDate = Date.now();
					let msOneWeek = 60 * 60 * 24 * 7 * 1000;
					let paymentInformation = {
						date: new Date(msCurrentDate - msOneWeek)
					};

					// act
					let result = component.getValidationErrors(paymentInformation);

					// assert
					expect(result.length).toBeGreaterThan(0);
				});

				it('should be post-date cannot be more than...', () => {
					// arrange
					let msCurrentDate = Date.now();
					let msFiftyDays = 60 * 60 * 24 * 50 * 1000;
					let paymentInformation = {
						date: new Date(msCurrentDate + msFiftyDays)
					};

					// act
					let result = component.getValidationErrors(paymentInformation);

					// assert
					expect(result.length).toBeGreaterThan(0);
				});

				it('should have no errors', () => {
					// arrange
					let msCurrentDate = Date.now();
					let maxFuture = 45;
					component.maxFuture = maxFuture;
					let msMaxFutureDays = 60 * 60 * 24 * (component.maxFuture) * 1000;
					let paymentInformation = {
						date: new Date(msCurrentDate + msMaxFutureDays)
					};

					// act
					let result = component.getValidationErrors(paymentInformation);

					// assert
					expect(result.length).toEqual(0);
				});
			});

			describe('submitTransaction()', () => {
				beforeEach(() => {
					component.paymentInformation = {};
					stubState.customer.accounts[0].displayName = 'testValue';
					component.accounts = stubState.customer.accounts;
				});

				describe('Apple Pay', () => {
					beforeEach(() => {
						component.isDigitalWallet = true;
						spyOn(ApplePayUtil, 'completeSession');
					});

					it('should submit Apple Pay single future sucessfully to Hpf Api', async () => {
						// Arrange
						const mockEvent: CustomEvent = mock(CustomEvent);
						const mockServiceResponse: ServiceResponse = mock(ServiceResponse);
						mockServiceResponse.value = {
							isSuccessful: true,
							hasException: false
						};

						component.isSingleFuture = true;
						component.paymentInformation.digitalWalletSettings = mockApplePaySettings;
						component.paymentInformation.addPaymentMethodToWalletIsSelected = false;

						await spyOn(component, 'submitToHpfApi')
							.and.returnValue(Promise.resolve(mockServiceResponse));

						spyOn(mockTemplateService, 'getPortalTemplateType')
							.and.returnValue(PortalTemplateTypes.CreditCardInstallmentPlanReceipt);

						// Act
						await component.submitTransaction(mockEvent);

						// Assert
						expect(component.paymentInformation.customerAccountNumber)
							.toEqual(stubState.customer.accounts[0].displayName);
						expect(ApplePayUtil.completeSession)
							.toHaveBeenCalledTimes(1);
						expect(component.paymentInformation.savedPaymentMethodToWallet)
							.toBeFalse();
						expect(component.paymentInformation.isFuturePayment)
							.toBeTrue();
					});

					it('should complete Apple Pay session when Hpf Api call fails', async () => {
						// Arrange
						component.isSingleFuture = true;
						component.paymentInformation.digitalWalletSettings = mockApplePaySettings;
						component.paymentInformation.addPaymentMethodToWalletIsSelected = false;

						await spyOn(component, 'submitToHpfApi')
							.and.returnValue(Promise.reject(anything()));

						// Act
						await component.submitTransaction(anything());

						// Assert
						expect(ApplePayUtil.completeSession)
							.toHaveBeenCalledTimes(1);
						expect(component.isSubmitting)
							.toBeFalse();
					});
				});
			});

			describe('submitToHpfApi()', () => {
				beforeEach(() => {
					component.paymentInformation = { date: new Date() };
					stubState.customer.accounts[0].balance = 1000;
					stubState.customer.accounts[0].adjustedBalances = { pendingBalance: Math.floor(Math.random() * 100) };
					stubState.customer.accounts[0].paymentPlans = [{ totalPayments: 10, paymentDate: new Date() }];
					component.accounts = stubState.customer.accounts;
					spyOn(NumberHelper, 'parseCurrency').and.returnValue(Math.floor(Math.random() * 10));
					spyOn(component.paymentItemTableViewModel, 'getSelectedItems').and.returnValue(mockAccountPaymentItems);
				});

				describe('ApplePay', () => {
					beforeEach(() => {
						component.isDigitalWallet = true;
						component.paymentInformation.digitalWalletSettings = mockApplePaySettings;
						spyOn(component, 'getDiscountAmountForAccountPaymentItem');
						spyOn(component, 'getDigitalWalletInfoForSubmission')
							.and
							.returnValue(mockDigitalWalletPaymentMethod);
					});

					it('should submit Apple Pay pay off early and call getDiscountAmountForAccountPaymentItem()', async () => {
						// Arrange
						const mockEvent: CustomEvent = mock(CustomEvent);
						const mockServiceResponse: ServiceResponse = mock(ServiceResponse);
						mockServiceResponse.value = {
							isSuccessful: true,
							hasException: false
						};

						component.isPayOffEarly = true;

						await spyOn(mockHpfTransactionService, 'submitTransaction')
							.and.returnValue(new Promise(() => mockServiceResponse));

						// Act
						await component.submitToHpfApi(mockEvent);

						// Assert
						expect(component.getDigitalWalletInfoForSubmission)
							.toHaveBeenCalledTimes(1);
						expect(component.getDiscountAmountForAccountPaymentItem)
							.toHaveBeenCalledTimes(1);
					});
				});
			});
		});
	});
});
